import { parseConfig } from '@utilities/theme';

/**
 * @class ProductBreadcrumbs
 * @extends HTMLElement
 * @description A custom element for displaying product breadcrumbs with a two-tier system.
 * Tier 1 represents the department (e.g., Men's, Women's), and Tier 2 represents a collection
 * the user navigated from. Tier 2 data is stored in sessionStorage and validated against the
 * product's collections to ensure relevance.
 */
class ProductBreadcrumbs extends HTMLElement {
  /**
   * @constructor
   * @description The constructor for the ProductBreadcrumbs class
   */
  constructor() {
    super();
  }

  /**
   * @description Checks if debug mode is enabled by presence of 'debug' class
   * @returns {boolean} True if debug mode is enabled
   */
  allowDebug() {
    return this.classList.contains('debug');
  }

  /**
   * @description Lifecycle callback when element is connected to the DOM.
   * Parses component configuration and initializes the breadcrumb display.
   */
  connectedCallback(){
    this.config = parseConfig(this, this.allowDebug()) || {};
    this.init();
  }

  /**
   * @description Initializes the component by rendering breadcrumbs and making the element visible.
   * Logs debug information if debug mode is enabled.
   */
  init(){
    if(this.allowDebug()) {
      console.log('ProductBreadcrumbs Debug:', {
        config: this.config,
        element: this
      });
    }

    this.setHTML();

    // show the breadcrumb element
    this.style.opacity = 1;
  }

  /**
   * @description Generates and renders the breadcrumb HTML based on tier 1 (department)
   * and tier 2 (collection) data. Tier 2 data is retrieved from sessionStorage and
   * validated to ensure the product belongs to the referenced collection.
   *
   * Breadcrumb hierarchy:
   * - Tier 1: Department level (e.g., "Women's", "Men's") from config
   * - Tier 2: Collection level (e.g., "Women's Polo Shirts") from sessionStorage
   *
   * Tier 2 validation:
   * - If tier 2 URL contains "/collections/", the collection handle is extracted and
   *   validated against this.config.collection_handles to ensure the product is in that collection
   * - Non-collection URLs (e.g., from link-list navigation) are used without validation
   * - Empty or invalid tier 2 data is safely ignored
   */
  setHTML(){
    this.replaceChildren();
    let breadcrumbsHTML = ``;
    let tier2CollectionHandle = null;
    let tier2CollectionTitle = null;

    // Check for tier 2 breadcrumb data in sessionStorage
    const tier2StorageData = sessionStorage.getItem('product_breadcrumbs_tier_2');
    if(tier2StorageData) {
      try {
        const tier2Data = JSON.parse(tier2StorageData);
        if(tier2Data.url && tier2Data.title) {
          // If the URL contains /collections/, validate it against product's collection handles
          if(tier2Data.url.indexOf('/collections/') >= 0) {
            const handle = tier2Data.url.split('/collections/')[1].split('?')[0].replace(/\/$/, '');
            // Only use tier 2 if this product is actually in that collection
            if(this.config.collection_handles && this.config.collection_handles.includes(handle)) {
              tier2CollectionHandle = tier2Data.url;
              tier2CollectionTitle = tier2Data.title;
            }
          } else {
            // Non-collection URLs (like link-list items) can be used directly
            tier2CollectionHandle = tier2Data.url;
            tier2CollectionTitle = tier2Data.title;
          }
        }
      } catch(e) {
        if(this.allowDebug()) {
          console.error('Error parsing tier 2 breadcrumb data:', e);
        }
      }
    }

    // get existing tier 1 breadcrumb (if any) from server-rendered HTML
    const existingTier1 = this.querySelector('a');
    let tier1HTML = '';
    if(existingTier1) {
      tier1HTML = existingTier1.outerHTML;
    }

    // build breadcrumbs HTML - prioritize config data over server-rendered HTML
    let tier1Link = '';
    if(this.config.tier_one_collection && this.config.tier_one_title) {
      tier1Link = `<a id="product-breadcrumbs-tier-1" href="${this.config.tier_one_collection}" class="product__breadcrumb--tier-1">${this.config.tier_one_title}</a>`;
    } else if(tier1HTML) {
      tier1Link = tier1HTML;
    }

    if(this.allowDebug()) {
      console.log('Breadcrumb Generation:', {
        tier2CollectionHandle,
        tier2CollectionTitle,
        existingTier1: existingTier1 ? existingTier1.outerHTML : null,
        tier1HTML,
        tier1Link,
        configTier1: {
          collection: this.config.tier_one_collection,
          title: this.config.tier_one_title
        }
      });
    }

    // build breadcrumbs HTML
    if(tier1Link && tier2CollectionHandle) {
      // both tiers: Tier 1 / Tier 2
      breadcrumbsHTML = `${tier1Link}<span class="product__breadcrumb__separator">&nbsp;/&nbsp;</span><a id="product-breadcrumbs-tier-2" href="${tier2CollectionHandle}" class="product__breadcrumb--tier-2"><span class="product__breadcrumb__caret">${window.theme.elements.svg_caret_left}</span>${tier2CollectionTitle}</a>`;
    } else if(tier1Link) {
      // tier 1 only
      breadcrumbsHTML = tier1Link;
    } else if(tier2CollectionHandle) {
      // tier 2 only
      breadcrumbsHTML = `<a id="product-breadcrumbs-tier-2" href="${tier2CollectionHandle}" class="product__breadcrumb--tier-2"><span class="product__breadcrumb__caret">${window.theme.elements.svg_caret_left}</span>${tier2CollectionTitle}</a>`;
    }

    if(this.allowDebug()) {
      console.log('Final breadcrumbsHTML:', breadcrumbsHTML);
    }

    // if we have breadcrumbs, append them to the product breadcrumbs element
    if(breadcrumbsHTML) {
      this.innerHTML = breadcrumbsHTML;
    } else if(this.allowDebug()) {
      console.warn('No breadcrumbs generated - element will remain empty');
    }
  }
}

customElements.define('product-breadcrumbs', ProductBreadcrumbs)
